import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScheduleChecklistProgress } from '@/hooks/useScheduleChecklistProgress';

interface Props {
  show: boolean;
  itemTitle: string;
  progress: ScheduleChecklistProgress;
  onConfirmManualComplete: () => void;
  onClose: () => void;
  canManage: boolean;
}

export default function ScheduleCompletionBlockModal({
  show,
  itemTitle,
  progress,
  onConfirmManualComplete,
  onClose,
  canManage,
}: Props) {
  const { t } = useTranslation();
  const [confirmStep, setConfirmStep] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  if (!show) return null;

  const isReady = progress.readyToComplete;
  const noChecklist = !progress.hasLinkedItems;
  const reasonRequired = confirmStep;

  const handleClose = () => {
    setConfirmStep(false);
    setOverrideReason('');
    onClose();
  };

  const handleManualConfirm = () => {
    if (!confirmStep) {
      setConfirmStep(true);
      return;
    }
    if (reasonRequired && !overrideReason.trim()) return;
    onConfirmManualComplete();
    setConfirmStep(false);
    setOverrideReason('');
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose}></div>
      <div className="relative bg-white w-full max-w-sm rounded-3xl overflow-hidden mx-4">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-[#1A1A1A] font-bold text-base">
            {isReady
              ? t('completionBlock.allApprovalsComplete')
              : canManage
              ? t('completionBlock.manualCompletion')
              : t('completionBlock.cannotCompleteYet')}
          </h3>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-[#374151]"></i>
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-[#6B7280] text-sm">
            <span className="font-semibold text-[#374151]">{itemTitle}</span>
          </p>

          {noChecklist ? (
            <div className="bg-[#FEF0E7] rounded-xl p-3 border border-[#FDBA74]">
              <div className="flex items-start gap-2">
                <i className="ri-error-warning-line text-[#F59E0B] text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-[#92400E] text-xs">
                  {canManage
                    ? t('completionBlock.noChecklist')
                    : t('completionBlock.noChecklistMgmtOnly')}
                </p>
              </div>
            </div>
          ) : !isReady ? (
            <div className="bg-[#F9FAFB] rounded-xl p-3 border border-gray-100 space-y-3">
              <div className="text-center">
                <p className="text-[#92400E] text-sm font-bold mb-1">{t('completionBlock.waitingForApprovals')}</p>
                <p className="text-[#9CA3AF] text-[10px]">
                  {progress.missingManagement > 0 && progress.missingSubcontractor > 0
                    ? t('completionBlock.mgmtPlusSub', { mgmt: progress.missingManagement, sub: progress.missingSubcontractor })
                    : progress.missingSubcontractor > 0
                      ? t('completionBlock.subPending', { sub: progress.missingSubcontractor, check: progress.missingSubcontractor === 1 ? t('completionBlock.check') : t('completionBlock.checks') })
                      : progress.missingManagement > 0
                        ? t('completionBlock.mgmtPending', { mgmt: progress.missingManagement, check: progress.missingManagement === 1 ? t('completionBlock.check') : t('completionBlock.checks') })
                        : t('completionBlock.requiredApprovalsIncomplete')}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#F0FDF4] rounded-lg p-2 border border-[#BBF7D0]">
                  <p className="text-[10px] font-semibold text-[#059669] mb-0.5">{t('completionBlock.management')}</p>
                  <p className="text-sm font-bold text-[#059669]">
                    {progress.managementCheckedCount} / {progress.managementCheckedCount + progress.missingManagement}
                  </p>
                  <p className="text-[10px] text-[#9CA3AF]">
                    {progress.missingManagement > 0 ? `${progress.missingManagement} ${t('completionBlock.pending')}` : t('completionBlock.allDone')}
                  </p>
                </div>
                <div className="bg-[#ECFEFF] rounded-lg p-2 border border-[#A5F3FC]">
                  <p className="text-[10px] font-semibold text-[#0891B2] mb-0.5">{t('completionBlock.subcontractor')}</p>
                  <p className="text-sm font-bold text-[#0891B2]">
                    {progress.subcontractorCheckedCount} / {progress.subcontractorCheckedCount + progress.missingSubcontractor}
                  </p>
                  <p className="text-[10px] text-[#9CA3AF]">
                    {progress.missingSubcontractor > 0 ? `${progress.missingSubcontractor} ${t('completionBlock.pending')}` : t('completionBlock.allDone')}
                  </p>
                </div>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#E8621A] transition-all"
                  style={{ width: `${progress.progressPct}%` }}
                />
              </div>
              <p className="text-[10px] text-center text-[#9CA3AF]">
                {t('completionBlock.approvalUnits', { completed: progress.totalCompletedUnits, total: progress.totalRequiredUnits })}
              </p>
            </div>
          ) : (
            <div className="bg-[#F0FDF4] rounded-xl p-3 border border-[#BBF7D0]">
              <div className="flex items-start gap-2">
                <i className="ri-checkbox-circle-line text-[#059669] text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-[#059669] text-xs font-medium">
                  {t('completionBlock.allApprovalsReady')}
                </p>
              </div>
            </div>
          )}

          {/* Reason input for manual override */}
          {confirmStep && canManage && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#374151]">
                {t('completionBlock.reasonForManual')} <span className="text-[#EF4444]">*</span>
              </label>
              <textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder={t('completionBlock.reasonPlaceholder')}
                rows={2}
                maxLength={500}
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none"
              />
              <p className="text-[10px] text-[#9CA3AF]">
                {t('completionBlock.provideReason')}
              </p>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
          <button
            onClick={handleClose}
            className="flex-1 py-3 rounded-xl bg-white border border-gray-200 text-[#374151] text-sm font-bold cursor-pointer whitespace-nowrap"
          >
            {t('completionBlock.cancel')}
          </button>

          {isReady && (
            <button
              onClick={() => { onConfirmManualComplete(); handleClose(); }}
              className="flex-1 py-3 rounded-xl bg-[#059669] text-white text-sm font-bold cursor-pointer whitespace-nowrap"
            >
              {t('completionBlock.markComplete')}
            </button>
          )}

          {/* CRITICAL FIX: Always show Override button for management, even with incomplete checklist */}
          {canManage && !isReady && (
            <button
              onClick={handleManualConfirm}
              disabled={reasonRequired && !overrideReason.trim()}
              className="flex-1 py-3 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {confirmStep ? t('completionBlock.confirmOverride') : t('completionBlock.overrideComplete')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}